import { COOKIE_KEYS } from '~/shared/constants/cookies';
import {
  DEFAULT_LANGUAGE,
  REGISTERED_LANGUAGES,
  type Language,
} from '~/shared/constants/i18n';

const LS_KEY = 'tolgee-language';
const COOKIE_NAME = COOKIE_KEYS.USER_PREFERRED_LANGUAGE;
const COOKIE_MAX_AGE_IN_SECONDS = 2592000;

function resolveLanguage(lang: string): Language {
  return REGISTERED_LANGUAGES.includes(lang as Language)
    ? (lang as Language)
    : DEFAULT_LANGUAGE;
}

function safeSetLocalStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemoveLocalStorage(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function getCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const match = document.cookie.split('; ').find(row => row.startsWith(prefix));
  if (!match) return undefined;
  const idx = match.indexOf('=');
  const raw = match.slice(idx + 1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function setCookie(name: string, value: string, maxAgeSeconds: number) {
  const encoded = encodeURIComponent(value);
  document.cookie = `${name}=${encoded}; path=/; max-age=${maxAgeSeconds}`;
}

function clearCookie(name: string) {
  document.cookie = `${name}=; path=/; max-age=0`;
}

export function getPersistedLanguage(): Language {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

  const fromLs = localStorage.getItem(LS_KEY);
  if (fromLs && REGISTERED_LANGUAGES.includes(fromLs as Language)) {
    return fromLs as Language;
  }

  const fromCookie = getCookie(COOKIE_NAME);
  if (fromCookie && REGISTERED_LANGUAGES.includes(fromCookie as Language)) {
    safeSetLocalStorage(LS_KEY, fromCookie);
    return fromCookie as Language;
  }

  return DEFAULT_LANGUAGE;
}

export function persistLanguage(language: string): void {
  if (typeof window === 'undefined') return;

  const resolved = resolveLanguage(language);
  if (resolved !== language) {
    console.warn(
      `Language ${language} is not supported. Using default language.`
    );
  }

  safeSetLocalStorage(LS_KEY, resolved);
  setCookie(COOKIE_NAME, resolved, COOKIE_MAX_AGE_IN_SECONDS);
}

export function clearPersistedLanguage(): void {
  if (typeof window === 'undefined') return;

  safeRemoveLocalStorage(LS_KEY);
  clearCookie(COOKIE_NAME);
}
